import styled from "styled-components"
import GridContainer from "@/components/common/GridContainer"
import GridCards from "@/components/common/Cards/Grid"
import Container from "@/components/common/Container"
import { useMemo } from "react"
import { getCssSizeValue, getHtmlFromRedactorField, getPaddingsClassFromData, isStringEmpty } from "@/utils/utils"
import Image, { formatImageCMSData } from "@/components/common/Image"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { formatToCardItem } from "@/components/common/Cards"
import { insetPaddingLeftCss, insetPaddingRightCss } from "@/styles/globalStyles"
import HighlightGrid from "@/components/HighlightGrid"
import LinkButton from "@/components/common/Buttons/LinkButton"
import { BUTTON_VARIANTS } from "@/constants"
import { useIsDesktop } from "@/hooks/useDevice"
import { getButtonLinkData } from "@/components/common/Buttons/Button"

const Wrapper = styled.div`
	overflow: hidden;
`

const StyledDescriptionWithImageWrapper = styled(GridContainer)`
	background: ${({ theme }) => theme.colors.platinum};
	margin-bottom: ${getCssSizeValue(63, BREAKPOINTS.DESKTOP)};
	position: relative;
	align-items: center;
	${insetPaddingRightCss};
	${insetPaddingLeftCss};

	padding: ${getCssSizeValue(56, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: ${getCssSizeValue(110, BREAKPOINTS.TABLET)} 0;
	}
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: ${getCssSizeValue(64, BREAKPOINTS.DESKTOP)};
		padding: unset;
	}
`

const StyledColumn = styled.div`
	&.first {
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: ${getCssSizeValue(30, BREAKPOINTS.MOBILE)};
		margin-bottom: ${getCssSizeValue(64, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
			gap: ${getCssSizeValue(48, BREAKPOINTS.TABLET)};
			margin: unset;
		}
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			gap: ${getCssSizeValue(32, BREAKPOINTS.DESKTOP)};
		}
	}

	&.second {
		.image {
			width: 100%;
			height: auto;
			aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "16/9"};
		}
	}
`

const ButtonsWrapper = styled.div`
	display: flex;
	gap: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	height: 100px;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		gap: var(--grid-gap);
		margin-top: ${getCssSizeValue(16)};
	}
`

export const ID = "wwd-category"

function formatData(data) {
	return {
		id: `${data?.id || ""}`,
		header: data?.header || "",
		description: data?.description || "",
		image: formatImageCMSData(data?.image || {}),
		shouldDisplayItemsInHighlightGrid: data?.shouldDisplayItemsInHighlightGrid,
		horizontalCards: data?.isHorizontalAspect || false,
		cardsData: data?.pages || [],
		paddings: getPaddingsClassFromData(data),
		firstButton: getButtonLinkData(data?.firstButton),
		secondButton: getButtonLinkData(data?.secondButton),
	}
}

export default function DescriptionWithImage({ data }) {
	const isDesktop = useIsDesktop()
	const {
		id,
		header,
		description,
		image,
		shouldDisplayItemsInHighlightGrid,
		horizontalCards,
		cardsData,
		paddings,
		firstButton,
		secondButton,
	} = formatData(data)

	const formattedCardsData = useMemo(() => {
		return cardsData.map((item) => formatToCardItem(item))
	}, [data])

	return (
		<Wrapper id={id} className={paddings}>
			<StyledDescriptionWithImageWrapper>
				<StyledColumn className="first col-mobile-8 col-tablet-4 col-desktop-6">
					<div className="typo-borna typo-borna-44" dangerouslySetInnerHTML={getHtmlFromRedactorField(header)} />
					<div className="typo-borna typo-borna-45" dangerouslySetInnerHTML={getHtmlFromRedactorField(description)} />
					{(firstButton || secondButton) && (
						<ButtonsWrapper>
							{!isStringEmpty(firstButton?.url || "") && <LinkButton {...firstButton} variant={BUTTON_VARIANTS.QUATERNARY} />}
							{!isStringEmpty(secondButton?.url || "") && <LinkButton {...secondButton} />}
						</ButtonsWrapper>
					)}
				</StyledColumn>
				{isDesktop && <div className="col-desktop-1" />}
				<StyledColumn className="second col-mobile-8 col-tablet-4 col-desktop-8" $aspectRatio={image.aspectRatio}>
					{!isStringEmpty(image.url) && <Image src={image.url} alt={image.alt} />}
				</StyledColumn>
			</StyledDescriptionWithImageWrapper>
			<Container>
				{shouldDisplayItemsInHighlightGrid ? (
					<HighlightGrid data={formattedCardsData} />
				) : (
					<GridCards data={formattedCardsData} horizontalCards={horizontalCards} />
				)}
			</Container>
		</Wrapper>
	)
}
